import { parseISO } from 'date-fns';

import { DossierId } from '../dossier/types';

import {
    AttachmentFromAPI,
    DiscussionAPIResponse,
    Message,
    MessageAttachment,
    AttachmentType,
    MessageFromAPI,
    NormalizedDiscussion,
    Pagination,
    PaginationFromAPI,
    MessageAttachmentFromAPI,
    ScheduledMessagesAPIResponse,
    ScheduledMessage,
    MessageStatus,
    MessageType,
} from './types';

const shouldDisplayMessage = (message: MessageFromAPI): boolean =>
    [
        MessageType.TravellerToAgent,
        MessageType.AgentToTraveller,
        MessageType.EvaneosNotificationToAgent,
    ].includes(message.type);

const messagesSerializer = (messages: { [key: string]: MessageFromAPI }): Message[] =>
    Object.values(messages).filter(shouldDisplayMessage).map(serializeMessageFromAPI);

const paginationSerializer = (pagination: PaginationFromAPI): Pagination => ({
    total: pagination.total,
    totalPages: pagination.total_pages,
    count: pagination.count,
    currentPage: pagination.current_page,
    links: pagination.links,
});

export const serializeMessageFromAPI = (message: MessageFromAPI): Message => ({
    id: message.id,
    quoteId: message.quote_id,
    isFromTraveller: message.type === MessageType.TravellerToAgent,
    isFromAgent: message.type === MessageType.AgentToTraveller,
    isEvaneosNotification: message.type === MessageType.EvaneosNotificationToAgent,
    sendDate: parseISO(message.send_date),
    updatedAt: parseISO(message.updated_at),
    authorName: `${message.author_firstname} ${message.author_lastname}`,
    authorId: message.author_id.toString(),
    isMessageRead: message.status === MessageStatus.Read,
    body: message.body,
    htmlBody: message.body_html,
    attachments: message.attachments.map((attachment: AttachmentFromAPI) =>
        serializeMessageAttachmentFromDiscussionApi(attachment),
    ),
    messageIdFromAPI: message.message_id,
    provenance: message.provenance ?? undefined,
});

export const serializeMessageAttachmentFromDiscussionApi = (
    attachment: AttachmentFromAPI,
): MessageAttachment => ({
    name: attachment.path.replace(/\[.*]/, ''),
    type: attachment.type.replace(/.*\//, '') as AttachmentType,
    id: attachment.id,
});

export const serializeMessageAttachmentFromApiV2 = (
    attachment: MessageAttachmentFromAPI,
): MessageAttachment => ({
    name: attachment.name,
    link: attachment.url,
    type: attachment.type.replace(/.*\//, '') as AttachmentType,
    messageId: attachment.messageId,
});

export const serializeDiscussionFromAPI = (
    json: DiscussionAPIResponse,
    dossierId: DossierId,
): NormalizedDiscussion => ({
    [dossierId]: {
        messages: messagesSerializer(json.data),
        pagination: paginationSerializer(json.pagination),
        isResponseRequired: json.discussion.is_response_required,
    },
});

export const serializeScheduledMessageFromAPI = (
    json: ScheduledMessagesAPIResponse,
): ScheduledMessage => ({
    id: json.id,
    content: json.content,
    contentHtml: json.content_html,
    scheduledDate: parseISO(json.scheduled_at),
    authorId: json.author_id,
    attachments: json.attachments.map((attachment: AttachmentFromAPI) =>
        serializeMessageAttachmentFromDiscussionApi(attachment),
    ),
});
